import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAdminAuth } from './context/AdminAuthContext';
import Shell from './components/Shell';
import SignIn from './pages/SignIn';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import CreateProduct from './pages/CreateProduct';
import EditProduct from './pages/EditProduct';
import EditProductFields from './pages/EditProductFields';
import Categories from './pages/Categories';
import CreateCategory from './pages/CreateCategory';
import EditCategory from './pages/EditCategory';
import Filters from './pages/Filters';
import Coupons from './pages/Coupons';
import CreateCoupon from './pages/CreateCoupon';
import FontsList from './pages/FontsList';
import SaveFonts from './pages/SaveFonts';
import Watermarks from './pages/Watermarks';
import BrandingLogo from './pages/BrandingLogo';
import Songs from './pages/Songs';
import Halls from './pages/Halls';
import Shuls from './pages/Shuls';
import PeopleNames from './pages/PeopleNames';
import AutofillableFields from './pages/AutofillableFields';
import FieldLayoutPresets from './pages/FieldLayoutPresets';
import OrderList from './pages/OrderList';
import OrderItemEditor from './pages/OrderItemEditor';
import Analytics from './pages/Analytics';
import AdLinks from './pages/AdLinks';
import SystemLogs from './pages/SystemLogs';
import AdminUsers from './pages/AdminUsers';

// `requireAdminOnly` Routes are admin-only. The backend also gates these
// with requireAdminRole — this is the UI half of defense in depth, plus a
// nicer redirect than a 403 response.
function Gate({ children, requireAdminOnly }) {
  const { admin, ready } = useAdminAuth();
  if (!ready) return <div style={{ padding: 40 }}>Loading…</div>;
  if (!admin) return <Navigate to="/sign-in" replace />;
  const isEditor = admin.role === 'editor';
  // Editor trying to reach an admin-only route → bounce to products list.
  if (isEditor && requireAdminOnly) return <Navigate to="/pages/products" replace />;
  return <Shell>{children}</Shell>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/" element={<Gate requireAdminOnly><Dashboard /></Gate>} />
      <Route path="/pages/products" element={<Gate><Products /></Gate>} />
      <Route path="/pages/createProduct" element={<Gate><CreateProduct /></Gate>} />
      <Route path="/pages/editProduct/:id" element={<Gate><EditProduct /></Gate>} />
      <Route path="/pages/editProductFields/:id" element={<Gate><EditProductFields /></Gate>} />
      <Route path="/pages/categories" element={<Gate requireAdminOnly><Categories /></Gate>} />
      <Route path="/pages/createCategory" element={<Gate requireAdminOnly><CreateCategory /></Gate>} />
      <Route path="/pages/editCategory/:id" element={<Gate requireAdminOnly><EditCategory /></Gate>} />
      <Route path="/pages/filters" element={<Gate requireAdminOnly><Filters /></Gate>} />
      <Route path="/pages/coupons" element={<Gate requireAdminOnly><Coupons /></Gate>} />
      <Route path="/pages/createCoupon" element={<Gate requireAdminOnly><CreateCoupon /></Gate>} />
      <Route path="/pages/fontsList" element={<Gate requireAdminOnly><FontsList /></Gate>} />
      <Route path="/pages/saveFonts" element={<Gate requireAdminOnly><SaveFonts /></Gate>} />
      <Route path="/pages/watermarks" element={<Gate requireAdminOnly><Watermarks /></Gate>} />
      <Route path="/pages/brandingLogo" element={<Gate requireAdminOnly><BrandingLogo /></Gate>} />
      <Route path="/pages/songs" element={<Gate requireAdminOnly><Songs /></Gate>} />
      <Route path="/pages/halls" element={<Gate requireAdminOnly><Halls /></Gate>} />
      <Route path="/pages/shuls" element={<Gate requireAdminOnly><Shuls /></Gate>} />
      <Route path="/pages/peopleNames" element={<Gate requireAdminOnly><PeopleNames /></Gate>} />
      <Route path="/pages/autofillableFields" element={<Gate requireAdminOnly><AutofillableFields /></Gate>} />
      <Route path="/pages/fieldLayoutPresets" element={<Gate requireAdminOnly><FieldLayoutPresets /></Gate>} />
      <Route path="/pages/OrderList" element={<Gate requireAdminOnly><OrderList /></Gate>} />
      <Route path="/pages/orderItem/:orderId/:itemId" element={<Gate requireAdminOnly><OrderItemEditor /></Gate>} />
      <Route path="/pages/analytics" element={<Gate requireAdminOnly><Analytics /></Gate>} />
      <Route path="/pages/adLinks" element={<Gate requireAdminOnly><AdLinks /></Gate>} />
      <Route path="/pages/adminUsers" element={<Gate requireAdminOnly><AdminUsers /></Gate>} />
      <Route path="/pages/system-logs" element={<Gate requireAdminOnly><SystemLogs /></Gate>} />
      <Route path="*" element={<Navigate to="/pages/products" replace />} />
    </Routes>
  );
}
